export default {
  common: {
    documentTitle: 'GPTPlus | CDK Exchange',
    back: '← Back',
    backHome: '← Back to Home',
    loading: 'Loading...',
    submitting: 'Submitting...',
    querying: 'Querying...',
    retry: 'Search Again',
    selectPlaceholder: 'Select',
  },
  language: {
    switchToEnglish: 'Switch to English',
    switchToChinese: 'Switch to Chinese',
  },
  theme: {
    switchToLight: 'Switch to light mode',
    switchToDark: 'Switch to dark mode',
    lightMode: 'Light Mode',
    darkMode: 'Dark Mode',
  },
  home: {
    brand: 'Service Portal',
    brandSub: 'Account Upgrade Service',
    heroTitle: 'Recharge & Account Upgrade Service',
    heroSub: 'Verify your account and CDK, then confirm — step by step',
    flowTitle: 'How It Works',
    tabsAria: 'Recharge portal sections',
    announcementLabel: 'Notice',
    announcementAria: 'Recharge page announcement',
    services: {
      recharge: { title: 'Recharge', en: 'Service Portal', cta: 'Start Recharge', p1: 'Verify CDK code', p2: 'Submit session', p3: 'Confirm submission' },
      lookup: { title: 'Task Lookup', en: 'Task Lookup', cta: 'Check Status', p1: 'Look up by CDK', p2: 'View task status', p3: 'Track progress' },
    },
    flows: {
      submit: { title: 'Submit Flow', s1: 'Verify CDK — match plan and status', s2: 'Submit session — paste the JSON from chatgpt.com/api/auth/session', s3: 'Confirm and submit' },
      lookup: { title: 'Lookup Flow', s1: 'Enter CDK — the code used at purchase', s2: 'Query task — click the search button', s3: 'View status — see the matching task status' },
    },
  },
  queue: {
    pendingLabel: 'Pending in Console',
    unit: 'tasks',
    loading: 'Syncing',
    syncing: 'Live sync on',
    reconnecting: 'Reconnecting automatically',
  },
  cancel: {
    title: 'Cancel Task',
    inputLabel: 'Enter the code to cancel',
    rules: 'Only tasks still queued and not yet started can be cancelled. The code returns to unused and can be submitted again.',
    checkButton: 'Check status',
    checking: 'Checking…',
    stateQueued: 'This code is queued and waiting',
    stateQueuedHint: 'It can be cancelled now. The code returns to unused so you can submit again.',
    stateRunning: 'Processing has already started',
    stateRunningHint: 'A task that has started cannot be cancelled. Contact support if it stalls.',
    stateIdle: 'No queued task for this code',
    stateIdleHint: 'It is ready to use — go to Recharge to submit.',
    stateInvalid: 'Cannot cancel',
    submitButton: 'Cancel this task',
    confirmAsk: 'Confirm? The code returns to unused and you will need to submit again.',
    confirmYes: 'Confirm',
    confirmNo: 'Never mind',
    cancelling: 'Cancelling…',
    doneTitle: 'Cancelled',
    doneHint: 'The code is back to unused. You can submit it again from Recharge.',
    errLookup: 'Lookup failed, please retry',
    errFailed: 'Cancellation failed, please check the status again',
  },
  refresh: {
    title: 'Replace CDK',
    inputLabel: 'Enter your current CDK',
    rules: 'Replacing swaps only the code itself — the recharge task, failure reason and usage status all stay as they are. The old code becomes void: it can neither be redeemed nor looked up, so be sure to save the new one. Only unused CDKs, or ones whose last recharge failed, can be replaced, and each CDK can be replaced a limited number of times.',
    submitButton: 'Replace CDK',
    refreshing: 'Replacing…',
    failedTitle: 'Cannot replace',
    failed: 'Failed to replace the CDK, please try again later',
    resultTitle: 'Replacement result',
    successTitle: 'CDK replaced — please save the new code',
    keepOldHint: 'Save the new CDK now — the old one is void, and this is the only time the new code is shown.',
    oldCode: 'Old CDK',
    newCode: 'New CDK',
    remaining: 'Replacements left',
    copyNewCode: 'Copy new CDK',
    copySuccess: 'New CDK copied',
    goRecharge: 'Recharge with the new CDK',
  },
  recharge: {
    serviceClosed: 'Service temporarily paused',
    title: 'Recharge',
    subtitle: 'Service Portal · Account recharge request',
    steps: { s1: 'Verify CDK', s2: 'Submit Session', s3: 'Confirm' },
    step1Title: 'Verify CDK Code',
    cdkLabel: 'CDK Code',
    cdkPlaceholder: 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx',
    cdkValid: '✓ CDK valid',
    cdkPending: 'This CDK is being processed',
    cdkPendingHint: 'Auto-checking… if the task fails it will become submittable again automatically, no refresh needed',
    planType: 'Plan Type',
    verifying: 'Verifying...',
    verifyButton: 'Verify CDK',
    next: 'Next →',
    prev: '← Previous',
    restart: 'Start Over',
    step2Title: 'Submit Session',
    progressAria: 'Recharge progress',
    sessionGuidePrefix: 'After logging into ChatGPT, open in your browser',
    sessionGuideSuffix: ', copy the full JSON returned, and paste it into the box below.',
    sessionLabel: 'Session JSON',
    step3Title: 'Confirm & Submit',
    sessionStatus: 'Session Status',
    sessionFilled: '✓ Provided',
    submitSuccessTitle: '✓ Request submitted successfully!',
    taskId: 'Task ID',
    submitSuccessHint: 'You can track the progress on the "Task Lookup" page',
    confirmSubmit: 'Confirm & Submit',
    errInvalidCdk: 'Invalid CDK',
    errVerifyFailed: 'Verification failed',
    errCreateFailed: 'Failed to create task',
    errConfirmFailed: 'Submission failed',
    errNetwork: 'Network error',
    errSessionRequired: 'Please paste the complete Session JSON',
    errSessionInvalidJson: 'Invalid Session JSON. Please copy and paste the complete JSON',
    errSessionMissingAccessToken: 'Incomplete Session JSON: accessToken was not found',
    errSessionMissingSessionToken: 'Incomplete Session JSON: sessionToken was not found',
    errSessionMissingEmail: 'Incomplete Session JSON: account email was not found',
    errSessionMissingPlanType: 'Incomplete Session JSON: planType was not found',
    errSessionPlanNotFree: 'Current account planType is {planType}. Only free accounts can be submitted',
  },
  history: {
    title: 'Task Lookup',
    subtitle: 'Task Lookup · Check recharge task status by CDK',
    queryTitle: 'Lookup by CDK',
    queryHint: 'Enter the CDK used at purchase; the matching task status will be shown here.',
    queryPlaceholder: 'Enter the CDK code used at purchase',
    batchQueryTitle: 'Batch CDK Lookup',
    batchQueryHint: 'Paste multiple CDKs at once (up to 100), separated by new lines, spaces, commas, or semicolons. Duplicates are removed automatically.',
    batchQueryPlaceholder: 'One CDK per line, or separate multiple codes with commas',
    detectedCodes: '{count} unique CDKs detected',
    clearQuery: 'Clear Query',
    cdkLabel: 'CDK Code',
    queryBtn: 'Query Task',
    batchQueryBtn: 'Batch Query',
    queryErrorTitle: 'Query Failed',
    resultTitle: 'Result',
    liveRefreshing: 'Auto-refreshing in-progress tasks…',
    batchResultTitle: 'Batch Results',
    batchSummaryTitle: 'Lookup Overview',
    batchSummaryText: '{found}/{total} tasks found, {unused} unused.',
    batchSummaryTextWithMissing: '{found}/{total} tasks found, {unused} unused, {notFound} not found.',
    batchStatsAria: 'Batch lookup statistics',
    totalQueried: 'Total',
    foundCount: 'Found',
    processingCount: 'Processing',
    failedCount: 'Failed',
    unusedCount: 'Unused',
    notFoundCount: 'Not Found',
    batchListAria: 'Batch lookup result list',
    accountUnknown: 'Account not recorded',
    unusedShort: 'Unused',
    unusedHint: 'This CDK has not been submitted for recharge',
    unusedWithPlan: 'No task submitted · {plan}',
    notFoundShort: 'Not Found',
    copyCode: 'Copy this CDK',
    copyCodeShort: 'Copy',
    copyCodeSuccess: 'Copied {code}',
    copyReusable: 'Copy Reusable ({count})',
    copyReusableHint: 'Copies unused and failed CDKs together — both can still be submitted again',
    copyReusableSuccess: 'Copied {count} reusable CDKs (unused + failed).',
    copyReusableFailed: 'Copy failed. Please select and copy manually.',
    maxBatchExceeded: 'You can query up to {max} CDKs at a time. Please split the list and try again.',
    taskId: 'Task ID',
    cdkCode: 'CDK Code',
    subscriptionType: 'Subscription Type',
    rechargeAccount: 'Recharge Account',
    currentStatus: 'Current Status',
    failureReason: 'Failure Reason',
    detailTitle: 'Task Details',
    submitTime: 'Submitted At',
    completeTime: 'Completed At',
    queryOther: 'Query Another Task',
    detailActionsAria: 'Task actions',
    copyCdk: 'Copy CDK',
    copyAccount: 'Copy Account',
    resubmit: 'Resubmit',
    copyCdkSuccess: 'CDK copied',
    copyAccountSuccess: 'Account copied',
    copyActionFailed: 'Copy failed. Please copy manually.',
    selectTaskTitle: 'Select a task to view details',
    selectTaskHint: 'Click any found task on the left to see its full details here.',
    notFoundTitle: 'No task found for this CDK',
    notFoundHint: 'Please check whether the CDK is correct',
    msgPending: '⏳ Task is waiting to be processed, please wait...',
    msgSubmitted: '⚙ Task is being processed, please wait...',
    msgManualReview: '⏳ Manual handling is in progress. Please wait...',
    msgCompleted: '✓ Task completed, your account has been upgraded',
    msgFailed: '✗ Task failed, please resubmit or contact support',
    resultHeadline: { pending: 'Waiting for Confirmation', submitted: 'Processing in Console', manual_review: 'Manual Handling', completed: 'Recharge Completed', failed: 'Recharge Failed' },
    status: { pending: 'Pending', submitted: 'Processing', manual_review: 'Manual Handling', completed: 'Completed', failed: 'Failed' },
  },
  subscription: {
    title: 'Subscription',
    inputLabel: 'Enter Session',
    inputPlaceholder: 'Paste the full JSON from chatgpt.com/api/auth/session, or an accessToken directly',
    inputHint: 'Used only to check the subscription status in real time; your session is never stored. Full session JSON or accessToken both work.',
    queryBtn: 'Check Subscription',
    clear: 'Clear',
    queryErrorTitle: 'Query Failed',
    queryFailed: 'Subscription lookup failed, please try again',
    errInvalidInput: 'Invalid session format. Paste the full session JSON or a bare accessToken.',
    errInvalidSession: 'Session is invalid or expired. Please get a fresh one and retry.',
    errRateLimited: 'Too many requests, please try again shortly.',
    resultTitle: 'Subscription',
    statusActive: 'Active subscription with auto-renewal on',
    statusCancelling: 'Active subscription, but auto-renewal is off',
    statusInactive: 'No active subscription on this account',
    badgeActive: 'Active',
    badgeInactive: 'None',
    cancelledNotice: 'Auto-renewal is off. The subscription will stop at the end of the current period with no further charges.',
    willRenewNotice: 'Auto-renewal is on. You will be charged again on {date}.',
    inactiveNotice: 'This account has no active subscription.',
    yes: 'Yes',
    no: 'No',
    renewOn: 'On',
    renewOff: 'Off',
    planFree: 'Free',
    detailTitle: 'Details',
    fieldAccount: 'Account',
    fieldPlan: 'Plan',
    fieldActive: 'Active',
    fieldWillRenew: 'Auto-renew',
    fieldExpires: 'Expires',
    fieldRenews: 'Renews',
    fieldCancels: 'Cancels at',
    fieldBillingPeriod: 'Billing period',
    fieldPlatform: 'Purchase platform',
    periodMonthly: 'Monthly',
    periodYearly: 'Yearly',
    periodWeekly: 'Weekly',
    platformWeb: 'ChatGPT Web',
    platformIos: 'iOS (App Store)',
    platformGoogle: 'Android (Google Play)',
  },
  login: {
    title: 'Admin Login',
    subtitle: 'Log in to access the admin console, CDK management and task processing.',
    username: 'Username',
    usernamePlaceholder: 'Enter username',
    password: 'Password',
    passwordPlaceholder: 'Enter password',
    rememberPassword: 'Remember password',
    rememberPasswordHint: 'Saved only in this browser and cleared when unchecked',
    loginBtn: 'Sign In',
    loggingIn: 'Signing in...',
    errLoginFailed: 'Login failed',
    errNetwork: 'Network error, please try again later',
  },
  notFound: {
    title: 'Page Not Found',
    home: 'Back to Home',
  },
}
